import type { MaybeElementRef } from '@vueuse/core'

/**
 * Share of a sticky PDP pane that must sit inside the viewport before that
 * pane scrolls on its own. Below this, wheel and touch move the page.
 * Matches the gallery threshold already shipped on the redesigned PDP.
 */
const PDP_PANE_OWN_SCROLL_MIN_RATIO = 0.8

/**
 * Locks a sticky PDP pane (`overflow-y: hidden`) while less than
 * {@link PDP_PANE_OWN_SCROLL_MIN_RATIO} of it is inside the viewport, so
 * scrolling over that pane moves the page. Desktop only: below `lg` the
 * columns are not sticky and the page scrolls natively.
 *
 * @param target The sticky pane element.
 * @returns `hasOwnScroll` and an inline style that hides overflow when the pane should not scroll on its own.
 */
export const usePdpPaneOwnScroll = (target: MaybeElementRef) => {
  const { breakpoint } = useProjectBreakpoint()
  const isLarge = breakpoint.greaterOrEqual('lg')
  const hasOwnScroll = ref(true)

  useIntersectionObserver(
    target,
    entries => {
      const ratio = entries[0]?.intersectionRatio ?? 0
      hasOwnScroll.value = ratio >= PDP_PANE_OWN_SCROLL_MIN_RATIO
    },
    { threshold: [0, PDP_PANE_OWN_SCROLL_MIN_RATIO, 1] }
  )

  const paneScrollStyle = computed(() =>
    isLarge.value && !hasOwnScroll.value
      ? ({ overflowY: 'hidden' } as const)
      : undefined
  )

  return {
    hasOwnScroll,
    paneScrollStyle,
  }
}
